import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
  ComponentType,
  Fragment,
  ReactNode,
  forwardRef,
} from 'react'
import styled from 'styled-components'
import { useTrackVisibility } from 'react-intersection-observer-hook'
import has from 'lodash/has'
import debounce from 'lodash/debounce'

import useDispatch from 'hooks/useDispatch'
import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import { resetSecondLevelFilters } from '../../actions/searchActions'
import {
  SearchQueryHookResult,
  useGetSelectEscapeIndexesQuery,
} from '../../hooks/useQueries'
import { media } from '../../styles/media'

import Button from '../../components/display/Button'
import SimpleA from '../../components/display/SimpleAnchor'
import PlaceholderCard from '../../components/cards/PlaceholderCard'
import DatePrice from '../../components/icons/brand/DatePrice'
import DatePopular from '../../components/icons/brand/DatePopular'
import InlineAlert from '../../components/display/InlineAlert'

/**
 * We need to disable scroll anchoring so when the user is scrolled
 * all the way down on chrome adding more elements to the page
 * scroll the user up so we can keep the load more loop going
 */
import DisableScrollAnchoring from '../../components/utils/DisableScrollAnchoring'
import { Grid, GridItem } from '../../components/layout/ItemGrid'

import Empty from './Empty'
import Result from './Result'
import buildApologiesMessage from './buildApologiesMessage'

const CustomActionWell = styled(InlineAlert)`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  margin: 0 16px;
  align-items: center;

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xs']?.['font-size']?.value};
    margin: 0;
  `}
`

const RemoveFiltersButton = styled(Button).attrs({
  rounded: true,
  ghost: true,
})`
  margin: 0 auto;
`

const LinkWrapper = styled.p`
  font-size: ${({ theme }) => theme['-ds-font-size-sm']['font-size'].value};
  margin-top: 24px;
`

const ResultTypeHeading = styled.div`
  width: 100%;
  margin: 32px 0 16px 0;
  padding: 0 16px;

  h3 {
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-md']?.['font-size']?.value};
    font-weight: 400;
    margin-bottom: 8px;
  }
`

const noResultsMessageForSearchType = (
  searchType: GQL_SearchFilters['type']
): ReactNode => {
  switch (searchType) {
    case 'accommodation':
      return "Uh-oh! We can't find any stays that match your search."
    case 'adventures':
      return "Uh-oh! We can't find any adventures that match your search."
    default:
      return undefined
  }
}

export const EmptySearchActions = ({
  onResetFilters,
}: {
  onResetFilters: () => any
}) => (
  <>
    <RemoveFiltersButton onClick={onResetFilters}>
      Remove Filters
    </RemoveFiltersButton>
    <LinkWrapper>
      Take me <SimpleA href="/">back home.</SimpleA>
    </LinkWrapper>
  </>
)

type LoaderProps = {
  variant?: 'Listing' | 'Story' | 'StoryAdventure' | 'ListingAdventure'
  lastResultSetCount?: number
}

const Loader = forwardRef<HTMLDivElement, LoaderProps>(
  ({ variant = 'Story', lastResultSetCount = 0 }, ref) => {
    // `GridItem` component defines the following number of cards per row:
    //   <  md = 1
    //   >= md = 2
    //   >= lg = 3
    //   >= xxxl = 4
    //
    // However on Desktop **if** `useWhichMapMode` returns `inline` (when >= xl)
    // this component is wrapped by `DesktopSearchView` which uses `ResultsArea`
    // to override these styles:
    //   >= xl = 2
    //   >= xxxl = 3
    //   >= xwide = 4
    //
    // Then on Mobile (or Desktop **if** `useWhichMapMode` returns `toggle`
    // i.e. when < xl) this component is wrapped by `MobileSearchView` which
    // users `ResultsWrapper` to override these styles:
    //   <  sm = 1
    //   >= sm = 2
    //   >= lg = 3
    //
    // Combined these mean:
    //   <  sm = 1
    //   >= sm = 2
    //   >= md = 2 (redundant)
    //   >= lg = 3
    //   >= xl = 2
    //   >= xxxl = 3
    //   >= xwide = 4

    const {
      xwide: atLeastXwide,
      xxxl: atLeastXxxl,
      xl: atLeastXl,
      lg: atLeastLg,
      sm: atLeastSm,
    } = useMediaQueryContext()

    const rowCardCount = useMemo(() => {
      if (atLeastXwide) return 4
      if (atLeastXxxl) return 3
      if (atLeastXl) return 2
      if (atLeastLg) return 3
      if (atLeastSm) return 2

      return 1
    }, [atLeastXwide, atLeastXxxl, atLeastXl, atLeastLg, atLeastSm])

    const placeholderCount = useMemo(() => {
      const resultsOverflow = lastResultSetCount % rowCardCount

      // If the results have overflowed onto the next row, include placeholders
      // to fill that row before adding a complete row of placeholders below:
      return resultsOverflow > 0
        ? rowCardCount + (rowCardCount - resultsOverflow)
        : rowCardCount
    }, [lastResultSetCount, rowCardCount])

    return (
      <>
        {[...Array(placeholderCount)].map((e, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <GridItem key={i} ref={i === 0 ? ref : undefined}>
            <PlaceholderCard portrait={variant === 'Story'} />
          </GridItem>
        ))}
      </>
    )
  }
)

type Data = SearchQueryHookResult['data']
type Defaults = Data['search']['defaults']
type ResultsType = Data['search']['results']

interface ResultsProps {
  defaults: Defaults
  fetchMore: (...args: any) => any
  filters: GQL_SearchFilters
  results: ResultsType
  loading?: boolean
  isDesktop?: boolean
}

const Results = ({
  defaults,
  fetchMore,
  filters,
  loading,
  isDesktop,
  results,
}: ResultsProps) => {
  const dispatch = useDispatch()

  const isNZ = filters?.country === 'NZ'

  // @ts-ignore -- resetSecondLevelFilters not typed yet
  const onResetFilters = () =>
    dispatch(resetSecondLevelFilters({ preserveStateParam: !isNZ }))

  const { data: selectEscapeIndexesData } = useGetSelectEscapeIndexesQuery()

  const bucketListItemsIndex = selectEscapeIndexesData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex

  const bookingAdventuresIndex = selectEscapeIndexesData?.currentUser
    ?.bookingAdventuresIndex as GQL_BookingAdventuresIndex

  const hasSpecificDates = has(filters, 'startDate') && has(filters, 'endDate')
  const hasWeekendMonths = has(filters, 'weekendMonths')

  const hasDatesFilters = hasSpecificDates || hasWeekendMonths

  const hideTotalPricePrompt =
    (filters && ['stories', 'adventures'].indexOf(filters.type) > -1) ||
    hasDatesFilters

  const showPercentageAvailable =
    hideTotalPricePrompt &&
    defaults &&
    defaults.percentageAvailable <= 50 &&
    defaults.percentageAvailable > 0 &&
    results &&
    results.edges.length > 0

  const noResultsMessage = noResultsMessageForSearchType(filters.type)

  const apologiesMessage = buildApologiesMessage(filters)

  const lastResult =
    results?.edges?.[(results?.edges?.length ?? 0) - 1]?.node?.result
  const loaderVariant = lastResult?.__typename

  const firstStoryResultIdRef = useRef<string>()
  const firstListingResultIdRef = useRef<string>()
  const lastResultSetCountRef = useRef<number>(0)

  useEffect(() => {
    firstStoryResultIdRef.current = null
    firstListingResultIdRef.current = null
    lastResultSetCountRef.current = 0
  }, [results, filters])

  const outerRef = useRef<HTMLDivElement>()

  const [ref, { isVisible }] = useTrackVisibility({
    rootMargin: isDesktop ? '10%' : '75% 0px',
  })

  const hasNextPage = results?.pageInfo?.hasNextPage

  useLayoutEffect(() => {
    if (isVisible && hasNextPage) {
      fetchMore()
    }
  }, [isVisible, hasNextPage])

  return (
    <div ref={outerRef}>
      <DisableScrollAnchoring />
      {!hideTotalPricePrompt && !hasDatesFilters && results?.edges?.length > 0 && (
        <CustomActionWell variant="information" iconWidth={32}>
          <DatePrice />
          <div>
            Select your preferred dates to see the total price inclusive of any
            additional charges and fees.
          </div>
        </CustomActionWell>
      )}
      {showPercentageAvailable && (
        <CustomActionWell variant="warning" iconWidth={32}>
          <DatePopular />
          <div>
            <strong>These dates are popular!</strong> Only{' '}
            {defaults.percentageAvailable}% of escapes are still available.
          </div>
        </CustomActionWell>
      )}
      <Grid data-testid="results-grid">
        {loading && !results && <Loader variant={loaderVariant} />}

        {!loading && results?.edges.length === 0 && (
          <Empty
            message={noResultsMessage}
            apologiesMessage={apologiesMessage}
            actions={<EmptySearchActions onResetFilters={onResetFilters} />}
          />
        )}

        {results?.edges.map(({ node }) => {
          if (
            node.result.__typename === 'Story' &&
            firstStoryResultIdRef.current == null
          ) {
            firstStoryResultIdRef.current = node.result.id
          }

          if (
            node.result.__typename === 'Listing' &&
            firstListingResultIdRef.current == null
          ) {
            lastResultSetCountRef.current = 0
            firstListingResultIdRef.current = node.result.id
          }

          lastResultSetCountRef.current += 1

          return (
            <Fragment key={`${node.result.__typename}:${node.result.id}`}>
              {firstStoryResultIdRef.current === node.result.id && (
                <ResultTypeHeading>
                  <h3>Curated escapes</h3>
                  <p>
                    Be inspired by real trips taken by Riparide Storytellers.
                  </p>
                </ResultTypeHeading>
              )}
              {firstListingResultIdRef.current === node.result.id && (
                <ResultTypeHeading>
                  <h3>Explore listings</h3>
                  <p>
                    Discover all the unique listings you can stay at in this
                    region.
                  </p>
                </ResultTypeHeading>
              )}
              <Result
                bookingAdventuresIndex={bookingAdventuresIndex}
                bucketListItemsIndex={bucketListItemsIndex}
                result={node.result}
                pricing={node.pricing}
                discount={node.discount}
                availableDates={hasWeekendMonths && node?.availableDates}
                filters={filters}
                isMobile={!isDesktop}
              />
            </Fragment>
          )
        })}

        {hasNextPage && (
          <Loader
            ref={ref}
            variant={loaderVariant}
            lastResultSetCount={lastResultSetCountRef.current}
          />
        )}
      </Grid>
    </div>
  )
}

const withDebouncedResults = (BaseComponent: ComponentType<ResultsProps>) => ({
  results,
  ...props
}: ResultsProps) => {
  const [debouncedResults, setDebouncedResults] = useState(null)

  const handleChange = useCallback(
    debounce(() => {
      setDebouncedResults(results)
    }, 100),
    [results]
  )

  useEffect(() => {
    handleChange()

    return () => {
      handleChange.cancel()
    }
  }, [results])

  return <BaseComponent results={debouncedResults || results} {...props} />
}

export default withDebouncedResults(Results)
